/**
 * InfographicRenderer — ultra-rich, interactive infographic card.
 *
 * Consumes the structured payload produced by the backend docgen infographic flow:
 *   { title, eyebrow, hero, kpis[], comparisons, distribution, process, timeline, facts[], summary }
 *
 * Steel & Ember brand. Framer Motion entrance, count-up numbers, animated bars and
 * ring chart, hover micro-interactions. Fully `prefers-reduced-motion` aware.
 */
import { useMemo, useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Zap, Calendar, IndianRupee, DollarSign, Users, Package, Leaf, Shield,
  Gauge, Droplet, BarChart3, Target, Factory, Globe, CheckCircle2, AlertTriangle,
  Clock, TrendingUp, Download, Sparkles,
} from 'lucide-react';

const ICONS = {
  bolt: Zap, calendar: Calendar, rupee: IndianRupee, dollar: DollarSign,
  team: Users, package: Package, leaf: Leaf, shield: Shield,
  efficiency: Gauge, water: Droplet, chart: BarChart3, target: Target,
  factory: Factory, gauge: Gauge, globe: Globe, check: CheckCircle2,
  alert: AlertTriangle, clock: Clock, trend: TrendingUp,
};

const TOKEN_TO_HEX = {
  ember: '#F26B1F',
  blue: '#6BA6F5',
  mint: '#6EE7B7',
  steel: '#1E4E8C',
  amber: '#F59E0B',
};
const hex = (tok, fallback = '#F26B1F') => TOKEN_TO_HEX[tok] || fallback;

const pickIcon = (name) => ICONS[(name || '').toLowerCase()] || BarChart3;

/** Animated count-up (respects reduced-motion) */
const CountUp = ({ value, duration = 1200 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20% 0px' });
  const [display, setDisplay] = useState(typeof value === 'number' ? 0 : value);

  useEffect(() => {
    if (!inView) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // parse numeric from value
    const numeric = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/[^\d.-]/g, ''));
    if (Number.isNaN(numeric) || prefersReduced) {
      setDisplay(value);
      return;
    }
    const suffix = typeof value === 'string' ? String(value).replace(/[\d.,\s-]/g, '') : '';
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const cur = numeric * eased;
      const formatted = Number.isInteger(numeric) ? Math.round(cur).toLocaleString() : cur.toFixed(1);
      setDisplay(suffix ? `${formatted}${suffix}` : formatted);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return <span ref={ref}>{display}</span>;
};

/** Donut / ring chart — cumulative arcs */
const DistributionRing = ({ segments = [], size = 220, stroke = 24 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  const total = useMemo(() => segments.reduce((a, s) => a + (Number(s.value) || 0), 0) || 1, [segments]);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const [hoverIdx, setHoverIdx] = useState(null);

  let offset = 0;
  const arcs = segments.map((s, i) => {
    const pct = (Number(s.value) || 0) / total;
    const len = pct * c;
    const arc = { key: i, seg: s, pct, len, offset, color: hex(s.color, ['#F26B1F', '#6BA6F5', '#6EE7B7', '#1E4E8C', '#F59E0B'][i % 5]) };
    offset += len;
    return arc;
  });

  return (
    <div ref={ref} className="flex items-center gap-5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                  stroke="var(--hairline)" strokeWidth={stroke} opacity="0.5" />
          {arcs.map((a) => (
            <motion.circle
              key={a.key}
              cx={size / 2} cy={size / 2} r={r}
              fill="none"
              stroke={a.color}
              strokeWidth={hoverIdx === a.key ? stroke + 4 : stroke}
              strokeLinecap="butt"
              strokeDasharray={`${a.len} ${c}`}
              strokeDashoffset={-a.offset}
              initial={{ opacity: 0, strokeDasharray: `0 ${c}` }}
              animate={inView ? { opacity: 1, strokeDasharray: `${a.len} ${c}` } : {}}
              transition={{ duration: 0.9, delay: a.key * 0.08, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setHoverIdx(a.key)}
              onMouseLeave={() => setHoverIdx(null)}
              style={{ cursor: 'pointer', transition: 'stroke-width 180ms' }}
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {hoverIdx !== null ? (
            <>
              <span className="ig-mono text-[10px]" style={{ color: 'var(--nexus-text-tertiary)' }}>
                {arcs[hoverIdx].seg.label?.toUpperCase()}
              </span>
              <span className="ig-display text-[28px] font-semibold" style={{ color: arcs[hoverIdx].color }}>
                {Math.round(arcs[hoverIdx].pct * 100)}%
              </span>
            </>
          ) : (
            <>
              <span className="ig-mono text-[10px]" style={{ color: 'var(--nexus-text-tertiary)' }}>TOTAL</span>
              <span className="ig-display text-[28px] font-semibold text-nexus-text-primary">
                {total}
              </span>
            </>
          )}
        </div>
      </div>
      <ul className="space-y-2 flex-1 min-w-0">
        {arcs.map((a) => (
          <li
            key={a.key}
            onMouseEnter={() => setHoverIdx(a.key)}
            onMouseLeave={() => setHoverIdx(null)}
            className="flex items-center gap-3 text-[12.5px] cursor-pointer"
            data-testid={`dist-seg-${a.key}`}
          >
            <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: a.color }} />
            <span className="flex-1 truncate text-nexus-text-secondary">{a.seg.label}</span>
            <span className="ig-mono text-[11px]" style={{ color: a.color }}>{Math.round(a.pct * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Horizontal bar comparison group */
const BarGroup = ({ items = [], max }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  const computedMax = max || Math.max(...items.map(i => Number(i.value) || 0), 1);
  return (
    <div ref={ref} className="space-y-3">
      {items.map((it, i) => {
        const v = Number(it.value) || 0;
        const pct = Math.min(100, (v / computedMax) * 100);
        const color = hex(it.color, ['#F26B1F', '#6BA6F5', '#6EE7B7', '#1E4E8C', '#F59E0B'][i % 5]);
        return (
          <div key={i} className="group" data-testid={`bar-${i}`}>
            <div className="flex items-baseline justify-between text-[12px] mb-1">
              <span className="text-nexus-text-secondary font-medium truncate">{it.label}</span>
              <span className="ig-mono text-[11px]" style={{ color }}>
                {it.value}{it.unit || ''}
              </span>
            </div>
            <div className="relative h-2 rounded-full overflow-hidden" style={{ background: 'var(--layer-2)' }}>
              <motion.div
                className="absolute left-0 top-0 h-full rounded-full"
                style={{ background: color, boxShadow: `0 0 12px ${color}55` }}
                initial={{ width: 0 }}
                animate={inView ? { width: `${pct}%` } : {}}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            {it.note && (
              <p className="text-[11px] text-nexus-text-tertiary mt-1 opacity-80 group-hover:opacity-100 transition-opacity">
                {it.note}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Horizontal process/step flow */
const ProcessFlow = ({ steps = [] }) => (
  <div className="relative">
    <div className="absolute left-4 right-4 top-6 h-px" style={{ background: 'var(--hairline)' }} />
    <motion.div
      className="absolute left-4 top-6 h-px"
      style={{ background: 'linear-gradient(90deg, #F26B1F, #6BA6F5)' }}
      initial={{ width: 0 }}
      whileInView={{ width: 'calc(100% - 32px)' }}
      viewport={{ once: true, margin: '-15% 0px' }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
    />
    <div className="grid gap-3 relative" style={{ gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
          className="flex flex-col items-center text-center px-1"
          data-testid={`process-step-${i}`}
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mb-3 relative z-10"
            style={{
              background: 'var(--layer-3)',
              border: `1.5px solid ${i === 0 ? '#F26B1F' : 'var(--hairline)'}`,
              boxShadow: i === 0 ? '0 0 0 6px rgba(242,107,31,0.08)' : 'none',
            }}
          >
            <span className="ig-mono text-[11px] font-semibold" style={{ color: '#F26B1F' }}>
              {s.step || `${i + 1}`.padStart(2, '0')}
            </span>
          </div>
          <h5 className="text-[12px] font-semibold text-nexus-text-primary mb-0.5">{s.label}</h5>
          {s.duration && (
            <span className="ig-mono text-[10px]" style={{ color: 'var(--nexus-accent-blue)' }}>
              {s.duration}
            </span>
          )}
          {s.description && (
            <p className="text-[11px] text-nexus-text-tertiary leading-snug mt-1">{s.description}</p>
          )}
        </motion.div>
      ))}
    </div>
  </div>
);

/** Vertical timeline */
const Timeline = ({ milestones = [] }) => (
  <ol className="relative space-y-3 pl-5" style={{ borderLeft: '1.5px dashed var(--hairline)' }}>
    {milestones.map((m, i) => (
      <motion.li
        key={i}
        initial={{ opacity: 0, x: -6 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: '-10% 0px' }}
        transition={{ duration: 0.35, delay: i * 0.06 }}
        className="relative"
        data-testid={`milestone-${i}`}
      >
        <span
          className="absolute -left-[26px] top-1 w-3 h-3 rounded-full"
          style={{
            background: i === 0 ? '#F26B1F' : 'var(--nexus-bg-primary)',
            border: `2px solid ${i === 0 ? '#F26B1F' : '#6BA6F5'}`,
          }}
        />
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="ig-mono text-[11px]" style={{ color: '#F26B1F' }}>{m.date}</span>
          <span className="text-[13px] font-semibold text-nexus-text-primary">{m.label}</span>
        </div>
        {m.note && <p className="text-[11px] text-nexus-text-tertiary mt-0.5">{m.note}</p>}
      </motion.li>
    ))}
  </ol>
);

/** The main card */
export const InfographicRenderer = ({ data, onDownloadSvg }) => {
  const printRef = useRef(null);
  if (!data || data.type !== 'infographic') return null;

  const {
    title, eyebrow, hero, kpis = [], comparisons, distribution,
    process, timeline, facts = [], summary, sections = [],
  } = data;

  // Fallback: if the backend only returned legacy `sections`, synthesise KPIs from them
  const displayKpis = kpis.length ? kpis : sections.slice(0, 4).map(s => ({
    icon: s.icon, label: s.label, value: s.value, description: s.description,
  }));

  const handleDownloadJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(title || 'infographic').replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.article
      ref={printRef}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, var(--layer-1) 0%, var(--layer-0) 100%)',
        border: '1px solid var(--hairline)',
        boxShadow: '0 12px 40px -10px rgba(0,0,0,0.35)',
      }}
      data-testid="infographic-card"
    >
      {/* Ember glow in corner */}
      <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-30 pointer-events-none"
           style={{ background: 'radial-gradient(circle, #F26B1F 0%, transparent 70%)' }} />

      {/* Header */}
      <header className="relative px-6 pt-6 pb-5 border-b" style={{ borderColor: 'var(--hairline)' }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-4 rounded-full" style={{ background: '#F26B1F' }} />
          <span className="ig-eyebrow text-[10px]" style={{ color: '#F26B1F' }}>
            {(eyebrow || 'INFOGRAPHIC')}
          </span>
          <span className="flex-1" />
          <button
            onClick={handleDownloadJSON}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border hover:bg-nexus-bg-tertiary/40 transition-colors"
            style={{ borderColor: 'var(--hairline)', color: 'var(--nexus-text-tertiary)' }}
            title="Download as JSON"
            data-testid="infographic-download-json"
          >
            <Download size={10} />
            JSON
          </button>
        </div>
        <h3 className="ig-display text-[20px] md:text-[24px] leading-[1.2] text-nexus-text-primary">
          {title}
        </h3>
      </header>

      {/* Hero stat */}
      {hero && (
        <div className="relative px-6 py-6 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-5 md:gap-8 items-center border-b" style={{ borderColor: 'var(--hairline)' }}>
          <div className="flex flex-col">
            <span className="ig-eyebrow text-[10px]" style={{ color: 'var(--nexus-accent-blue)' }}>
              {hero.label || 'HEADLINE STAT'}
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="ig-display font-semibold text-[48px] md:text-[56px] leading-none" style={{ color: '#F26B1F' }}>
                <CountUp value={hero.value} />
              </span>
              {hero.unit && (
                <span className="ig-display text-[22px] md:text-[26px] font-medium text-nexus-text-primary">
                  {hero.unit}
                </span>
              )}
            </div>
          </div>
          {hero.caption && (
            <p className="text-[13px] md:text-[14px] text-nexus-text-secondary leading-relaxed">
              {hero.caption}
            </p>
          )}
        </div>
      )}

      {/* KPI grid */}
      {displayKpis.length > 0 && (
        <section className="px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-3 border-b" style={{ borderColor: 'var(--hairline)' }}>
          {displayKpis.slice(0, 8).map((k, i) => {
            const Icon = pickIcon(k.icon);
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-10% 0px' }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                whileHover={{ y: -2 }}
                className="rounded-xl p-3 md:p-4 cursor-default"
                style={{
                  background: 'var(--layer-2)',
                  border: '1px solid var(--hairline)',
                }}
                data-testid={`kpi-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                       style={{ background: 'rgba(242,107,31,0.1)', color: '#F26B1F' }}>
                    <Icon size={14} strokeWidth={2} />
                  </div>
                  {k.unit && (
                    <span className="ig-mono text-[10px]" style={{ color: 'var(--nexus-text-tertiary)' }}>
                      {k.unit}
                    </span>
                  )}
                </div>
                <div className="ig-display text-[22px] md:text-[26px] font-semibold text-nexus-text-primary leading-tight">
                  <CountUp value={k.value} />
                </div>
                <div className="ig-eyebrow text-[9px] mt-1" style={{ color: '#F26B1F' }}>
                  {k.label}
                </div>
                {k.description && (
                  <p className="text-[11px] text-nexus-text-tertiary mt-1.5 leading-snug line-clamp-3">
                    {k.description}
                  </p>
                )}
              </motion.div>
            );
          })}
        </section>
      )}

      {/* Distribution ring + Comparison bars side-by-side */}
      {(distribution || comparisons) && (
        <section className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6 border-b" style={{ borderColor: 'var(--hairline)' }}>
          {distribution && (
            <div>
              <h4 className="ig-eyebrow text-[10px] mb-3" style={{ color: '#F26B1F' }}>
                {distribution.title || 'DISTRIBUTION'}
              </h4>
              <DistributionRing segments={distribution.segments || []} />
            </div>
          )}
          {comparisons && (
            <div>
              <h4 className="ig-eyebrow text-[10px] mb-3" style={{ color: '#F26B1F' }}>
                {comparisons.title || 'COMPARISON'}
              </h4>
              <BarGroup items={comparisons.items || []} max={comparisons.max} />
            </div>
          )}
        </section>
      )}

      {/* Process flow */}
      {process?.steps?.length > 0 && (
        <section className="px-6 py-5 border-b" style={{ borderColor: 'var(--hairline)' }}>
          <h4 className="ig-eyebrow text-[10px] mb-4" style={{ color: '#F26B1F' }}>
            {process.title || 'PROCESS'}
          </h4>
          <ProcessFlow steps={process.steps} />
        </section>
      )}

      {/* Timeline + Facts side-by-side */}
      {(timeline?.milestones?.length || facts.length) > 0 && (
        <section className="px-6 py-5 grid grid-cols-1 md:grid-cols-[1.5fr,1fr] gap-6 border-b" style={{ borderColor: 'var(--hairline)' }}>
          {timeline?.milestones?.length > 0 && (
            <div>
              <h4 className="ig-eyebrow text-[10px] mb-3" style={{ color: '#F26B1F' }}>
                {timeline.title || 'TIMELINE'}
              </h4>
              <Timeline milestones={timeline.milestones} />
            </div>
          )}
          {facts.length > 0 && (
            <div>
              <h4 className="ig-eyebrow text-[10px] mb-3" style={{ color: '#F26B1F' }}>
                QUICK FACTS
              </h4>
              <div className="flex flex-wrap gap-2">
                {facts.map((f, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.25, delay: i * 0.04 }}
                    whileHover={{ y: -1 }}
                    className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-full text-[11px]"
                    style={{
                      background: 'rgba(242,107,31,0.08)',
                      border: '1px solid rgba(242,107,31,0.25)',
                      color: 'var(--nexus-text-secondary)',
                    }}
                    data-testid={`fact-${i}`}
                  >
                    <span className="ig-eyebrow text-[9px]" style={{ color: '#F26B1F' }}>
                      {f.label}
                    </span>
                    <span className="font-semibold text-nexus-text-primary">{f.value}</span>
                  </motion.span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Summary */}
      {summary && (
        <footer className="relative px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: 'rgba(107,166,245,0.12)', color: 'var(--nexus-accent-blue)' }}>
              <Sparkles size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="ig-eyebrow text-[10px] mb-1.5" style={{ color: 'var(--nexus-accent-blue)' }}>
                EXECUTIVE SUMMARY
              </h4>
              <p className="text-[13px] text-nexus-text-secondary leading-relaxed">{summary}</p>
            </div>
          </div>
        </footer>
      )}
    </motion.article>
  );
};

export default InfographicRenderer;
